import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6">
      <div className="w-full max-w-sm bg-white rounded-xl border border-slate-200 shadow-sm p-7 sm:p-9">
        <Link to="/" className="flex items-center gap-2 mb-6">
          <img src="/assmang-logo.png" alt="Assmang logo" className="h-9 w-auto" />
          <span className="font-display font-bold text-slate-900">ASSMANG SMME PORTAL</span>
        </Link>

        {done ? (
          <div className="text-center">
            <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-6 w-6 text-emerald-600" />
            </div>
            <h1 className="font-display text-2xl font-black text-slate-900">Password updated</h1>
            <p className="text-slate-500 mt-2 text-sm">Redirecting you to sign in…</p>
          </div>
        ) : !token ? (
          <div className="text-center">
            <h1 className="font-display text-2xl font-black text-slate-900">Invalid link</h1>
            <p className="text-slate-500 mt-2 text-sm">This reset link is missing or invalid. Please request a new one.</p>
            <Link to="/forgot-password" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#00427A] hover:underline">
              Request a new link
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-black text-slate-900 tracking-tight">Set new password</h1>
            <p className="text-slate-500 mt-2 mb-8 text-sm">Choose a new password for your account.</p>
            {error && (
              <div data-testid="reset-error" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>
            )}
            <form onSubmit={submit}>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>
              <input data-testid="reset-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none mb-4" placeholder="••••••••" />
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm Password</label>
              <input data-testid="reset-confirm-input" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-md border border-slate-300 focus:border-[#00427A] focus:ring-2 focus:ring-[#00427A]/20 outline-none mb-6" placeholder="••••••••" />
              <button data-testid="reset-submit-button" disabled={loading}
                className="w-full bg-[#00427A] hover:bg-[#00325C] text-white font-semibold py-2.5 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Update Password
              </button>
            </form>
            <p className="text-sm text-center mt-6">
              <Link to="/login" className="text-[#00427A] font-semibold hover:underline inline-flex items-center gap-1.5">
                <ArrowLeft className="h-4 w-4" /> Back to Sign In
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
